"use client";

import React, { useEffect, useMemo, useRef, type ReactNode, type ComponentType } from "react";
import { produce } from "immer";
import { EditorContext } from "../hooks/context";
import { createStore } from "../state/store";
import { createHistory } from "../state/history";
import { createActions } from "../state/actions";
import { createResolver } from "../resolver/resolver";
import { createNodeHelpers } from "../state/selectors";
import { serializeState, serializeNodes } from "../serialization/serialize";
import { parseReactElement } from "../serialization/parse-react";
import type { QueryMethods, EditorOptions, EditorState, FreshNodeInput, Node, CraftConfig } from "../state/types";

interface EditorProps {
  children: ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolver: Record<string, ComponentType<any>>;
  enabled?: boolean;
  indicator?: { success?: string; error?: string; thickness?: number };
  onNodesChange?: (query: QueryMethods) => void;
}

export function Editor({
  children,
  resolver: resolverMap,
  enabled = true,
  indicator,
  onNodesChange,
}: EditorProps) {
  const connectorRegistry = useMemo(() => new Map<string, HTMLElement>(), []);

  const storeRef = useRef<ReturnType<typeof createStore> | null>(null);
  const historyRef = useRef<ReturnType<typeof createHistory> | null>(null);
  const resolverRef = useRef<ReturnType<typeof createResolver> | null>(null);

  // Initialize once
  if (!storeRef.current) {
    const options: EditorOptions = {
      enabled,
      resolver: resolverMap,
      indicator: {
        success: indicator?.success ?? "#2d9d78",
        error: indicator?.error ?? "transparent",
        thickness: indicator?.thickness ?? 2,
      },
      onNodesChange,
    };

    storeRef.current = createStore(options);
    historyRef.current = createHistory();
    resolverRef.current = createResolver(resolverMap);
  }

  const store = storeRef.current;
  const history = historyRef.current!;
  const resolver = resolverRef.current!;

  // Update enabled state
  useEffect(() => {
    const state = store.getState();
    if (state.options.enabled !== enabled) {
      store.setState(
        produce(state, (draft: EditorState) => {
          draft.options.enabled = enabled;
        }),
      );
    }
  }, [enabled, store]);

  // Build query object
  const query: QueryMethods = useMemo(
    () => ({
      node(id: string) {
        return createNodeHelpers(store.getState(), id);
      },
      serialize() {
        return serializeState(store.getState(), resolver);
      },
      parseReactElement(element: React.ReactElement) {
        return {
          toNodeTree() {
            return parseReactElement(element);
          },
        };
      },
      parseFreshNode(input: FreshNodeInput) {
        return {
          toNode() {
            const { type, displayName, props, isCanvas, custom } = input.data;
            const craft = (type as unknown as { craft?: CraftConfig }).craft;
            const nodeId = `node-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const resolvedDisplayName =
              displayName ?? craft?.displayName ?? (type.displayName || type.name || "Unknown");
            const node: Node = {
              id: nodeId,
              dom: null,
              data: {
                type,
                isCanvas: isCanvas ?? craft?.canvas ?? false,
                props: { ...(craft?.props ?? {}), ...(props ?? {}) },
                displayName: resolvedDisplayName,
                name: resolvedDisplayName,
                custom: { ...(craft?.custom ?? {}), ...(custom ?? {}) },
                parent: null,
                nodes: [],
                linkedNodes: {},
                hidden: false,
              },
              events: { selected: false, hovered: false, dragged: false },
              related: craft?.related ?? {},
              rules: {
                canDrag: (craft?.rules?.canDrag as Node["rules"]["canDrag"]) ?? (() => true),
                canDrop: (craft?.rules?.canDrop as Node["rules"]["canDrop"]) ?? (() => true),
                canMoveIn: (craft?.rules?.canMoveIn as Node["rules"]["canMoveIn"]) ?? (() => true),
                canMoveOut: (craft?.rules?.canMoveOut as Node["rules"]["canMoveOut"]) ?? (() => true),
              },
            };
            return { rootNodeId: nodeId, nodes: { [nodeId]: node } };
          },
        };
      },
      getSerializedNodes() {
        return serializeNodes(store.getState(), resolver);
      },
      getNodes() {
        return store.getState().nodes;
      },
      getState() {
        return store.getState();
      },
      history: {
        canUndo: () => history.canUndo(),
        canRedo: () => history.canRedo(),
      },
    }),
    [store, resolver, history],
  );

  const actions = useMemo(
    () => createActions(store.getState.bind(store), store.setState.bind(store), history, resolver),
    [store, history, resolver],
  );

  // onNodesChange callback
  const onNodesChangeRef = useRef(onNodesChange);
  onNodesChangeRef.current = onNodesChange;
  const contentRevisionRef = useRef(store.getState().contentRevision);
  const pendingNodesChangeFrameRef = useRef<number | null>(null);

  useEffect(() => {
    const unsubscribe = store.subscribe(() => {
      const nextRevision = store.getState().contentRevision;
      if (nextRevision === contentRevisionRef.current) return;
      contentRevisionRef.current = nextRevision;

      if (pendingNodesChangeFrameRef.current !== null) return;
      pendingNodesChangeFrameRef.current = requestAnimationFrame(() => {
        pendingNodesChangeFrameRef.current = null;
        onNodesChangeRef.current?.(query);
      });
    });

    return () => {
      unsubscribe();
      if (pendingNodesChangeFrameRef.current !== null) {
        cancelAnimationFrame(pendingNodesChangeFrameRef.current);
        pendingNodesChangeFrameRef.current = null;
      }
    };
  }, [store, query]);

  const ctxValue = useMemo(
    () => ({ store, actions, query, resolver, history, connectorRegistry }),
    [store, actions, query, resolver, history, connectorRegistry],
  );

  return (
    <EditorContext.Provider value={ctxValue}>
      {children}
    </EditorContext.Provider>
  );
}
